/**
 * Per-column paginated task fetching hook for the Kanban board.
 *
 * Uses cursor-based pagination: the Apollo cache appends pages when an
 * `after` cursor is present, so data.tasks.items always holds the full
 * accumulated list. No local accumulation state is needed here.
 */

import { useState, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import type { ApolloError } from "@apollo/client";
import { GET_TASKS, buildGraphQLFilter, type TaskFilterParams } from "./useTasks";
import type { TaskStatus, TaskExtended } from "@/types";

const PAGE_SIZE = 20;

interface TasksQueryResult {
  tasks: {
    items: TaskExtended[];
    totalCount: number;
    hasNextPage: boolean;
    endCursor: string | null;
  };
}

export interface UseColumnTasksResult {
  tasks: TaskExtended[];
  totalCount: number;
  hasNextPage: boolean;
  loading: boolean;
  isFetchingMore: boolean;
  error: ApolloError | undefined;
  fetchNextPage: () => void;
  refetch: () => void;
}

export function useColumnTasks(
  status: TaskStatus,
  filterParams: TaskFilterParams,
  skip?: boolean,
  statuses?: TaskStatus[],
): UseColumnTasksResult {
  const filter = useMemo(
    () => buildGraphQLFilter({
      ...filterParams,
      ...(statuses ? { statuses } : { status }),
      parentIdIsNull: true,
    }),
    // Depend on a stable JSON key to avoid extra fetches on referential changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify({ ...filterParams, status, statuses })],
  );

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Synchronous in-flight guard. `isFetchingMore` is React state, so it is
  // not visible to the several extra renders that fire between a fetchMore
  // call and the state commit (virtualiser re-measure, notifyOnNetworkStatus,
  // cache churn from the open TaskDialog). Those renders would otherwise each
  // pass the state guard and fire another fetchMore with the *same* cursor —
  // a runaway flood of identical requests. The ref flips synchronously so any
  // re-entrant call within the same tick bails immediately.
  const fetchingRef = useRef(false);

  const { data, loading, error, fetchMore, refetch: apolloRefetch } = useQuery<TasksQueryResult>(
    GET_TASKS,
    {
      variables: { limit: PAGE_SIZE, filter },
      // Must read from the cache so the `fetchMore` cursor pages (accumulated
      // by the `tasks` field merge policy) are actually reflected in `data`.
      // A `network-only` policy displays only the latest network response
      // (page 1), so the appended pages never surface — `endCursor` stays on
      // page 1 and the auto-pagination effect re-requests the same next page
      // forever. `cache-and-network` still fetches fresh data on mount.
      fetchPolicy: "cache-and-network",
      skip,
      notifyOnNetworkStatusChange: true,
    },
  );

  const tasks = (data?.tasks?.items ?? []) as TaskExtended[];
  const endCursor = data?.tasks?.endCursor ?? null;

  const fetchNextPage = useCallback(async () => {
    if (
      fetchingRef.current ||
      isFetchingMore ||
      !data?.tasks?.hasNextPage ||
      !endCursor ||
      skip
    )
      return;
    fetchingRef.current = true;
    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: { limit: PAGE_SIZE, after: endCursor, filter },
      });
    } finally {
      fetchingRef.current = false;
      setIsFetchingMore(false);
    }
  }, [isFetchingMore, data, endCursor, skip, fetchMore, filter]);

  const refetch = useCallback(() => {
    // No `after` → hits the merge function's "full replace" branch.
    apolloRefetch({ limit: PAGE_SIZE, filter });
  }, [apolloRefetch, filter]);

  return {
    tasks,
    totalCount: data?.tasks?.totalCount ?? 0,
    hasNextPage: data?.tasks?.hasNextPage ?? false,
    loading,
    isFetchingMore,
    error,
    fetchNextPage,
    refetch,
  };
}

export { PAGE_SIZE as COLUMN_PAGE_SIZE };
